/*
 * Flow:
 * 1. User submits login form → SIGNIN_USER action dispatched
 * 2. Auth saga calls login API; on success dispatches userSignInSuccess(data, fromLogin=true)
 *    - fromLogin=true is set ONLY here; cross-tab sync and page-refresh restore pass false
 * 3. Auth reducer stores authUser + sets fromLogin=true
 * 4. Modal opens when: email is set AND policy_accepted is false AND fromLogin=true
 *    - Step 1: Terms & Conditions (must tick checkbox → Next)
 *    - Step 2: Privacy Policy (must tick checkbox → Accept)
 * 5. Accept → acceptPrivacyAndTermCondition API call
 *    - on result_code "0": dispatches acceptPolicySuccess → policy_accepted=true, fromLogin=false → modal closes
 *    - on 403: dispatches userSignOut (session expired)
 * 6. Decline (currently hidden): dispatches userSignOut + clears localStorage
 */
import {
  POLICY_CONTENT,
  POLICY_LANG_LABELS,
  TERMS_CONTENT,
  PolicyLang,
} from '@/config/privacy_policy_and_term_condition.config';
import { acceptPolicySuccess, userSignOut } from 'appRedux/actions/Auth';
import { Modal, Dropdown, MenuProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'ReducerState';
import { motion, AnimatePresence, AnimatePresenceProps } from 'framer-motion';
import {
  ShieldCheck,
  Check,
  X,
  FileText,
  ChevronRight,
  ChevronLeft,
  Globe,
  ChevronDown,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { acceptPrivacyAndTermCondition } from '@/apiClient/apiClient';
import toast from 'react-hot-toast';
import IntlMessages from '@/util/IntlMessages';
import { switchLanguage } from '@/appRedux/actions';
import languageData from '@/containers/Topbar/languageData';

// framer-motion v4 + React 18: children is no longer implicit in React.FC
const AnimatePresenceCompat = AnimatePresence as React.ComponentType<
  AnimatePresenceProps & { children: React.ReactNode }
>;

interface LocaleData {
  locale: string;
}

const getLocaleLang = (): PolicyLang => {
  try {
    const raw = localStorage.getItem('locale');
    const data: LocaleData | null = raw ? (JSON.parse(raw) as LocaleData) : null;
    if (data?.locale === 'th') return 'th';
    if (data?.locale === 'zh') return 'ch';
  } catch {
    // ignore
  }
  return 'en';
};

const getModalContainer = () => {
  let el = document.getElementById('privacy-policy-modal-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'privacy-policy-modal-root';
    document.body.appendChild(el);
  }
  return el;
};

const PrivacyAndTermConditionModal = () => {
  const dispatch = useDispatch();
  const { email, policy_accepted } = useSelector((state: AppState) => state?.auth?.authUser || {});
  const fromLogin = useSelector((state: AppState) => state?.auth?.fromLogin ?? false);
  const policyAccepted = !!policy_accepted;
  const [lang, setLang] = useState<PolicyLang>('en');
  const [step, setStep] = useState<1 | 2>(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [acknowledgedPrivacy, setAcknowledgedPrivacy] = useState(false);

  useEffect(() => {
    if (email) {
      setLang(getLocaleLang());
    }
  }, [email]);

  const { mutate: acceptPrivacyAndTermConditionMutate } = useMutation({
    mutationFn: (accepted: boolean) => acceptPrivacyAndTermCondition(accepted),
    onSuccess: (data) => {
      console.log(data);
      if (data?.data?.result_code === '0') {
        dispatch(acceptPolicySuccess());
        return;
      }
      toast.error(data?.result_msg || 'Failed to accept privacy and term condition');
    },
    onError: (err: any) => {
      if (err.status === 403) {
        toast.error(err?.data?.message || 'Session timeout, please login again');
        dispatch(userSignOut());
        return;
      }

      toast.error('Failed to accept privacy and term condition');
    },
  });

  const handleAccept = () => {
    acceptPrivacyAndTermConditionMutate(true);
  };

  const handleDecline = () => {
    dispatch(userSignOut());
    localStorage.clear();
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handleBackStep = () => {
    setStep(1);
  };

  const privacyContent = POLICY_CONTENT[lang] || POLICY_CONTENT['en'];
  const termsContent = TERMS_CONTENT[lang] || TERMS_CONTENT['en'];

  const langItems: MenuProps['items'] = (
    Object.entries(POLICY_LANG_LABELS) as [PolicyLang, string][]
  ).map(([key, label]) => ({
    key,
    label: (
      <div
        className={`px-2 py-1 text-sm font-medium ${lang === key ? 'text-blue-600' : 'text-slate-700'}`}
      >
        {label}
      </div>
    ),
    onClick: () => {
      const targetLocale = key === 'ch' ? 'zh' : key;
      const localeObj = languageData.find((l) => l.locale === targetLocale) || languageData[0];
      localStorage.setItem('locale', JSON.stringify(localeObj));
      dispatch(switchLanguage(localeObj as any));
      setLang(key as PolicyLang);
    },
  }));

  const proseClass = `
    [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-slate-800 [&_h2]:mb-5 [&_h2]:tracking-tight
    [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:tracking-tight
    [&_p]:text-[15px] [&_p]:md:text-[16px] [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:mb-5
    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:md:pl-6 [&_ol]:space-y-4 [&_ol]:mb-6
    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:md:pl-6 [&_ul]:space-y-3 [&_ul]:mt-3
    [&_li]:text-[15px] [&_li]:md:text-[16px] [&_li]:text-slate-600 [&_li]:leading-relaxed
    [&_strong]:font-semibold [&_strong]:text-slate-800
    [&_a]:text-blue-500 [&_a]:underline [&_a:hover]:text-blue-600 transition-colors
  `;

  return (
    <Modal
      open={!!email && !policyAccepted && fromLogin}
      // open={true}
      getContainer={getModalContainer}
      closable={false}
      maskClosable={false}
      footer={null}
      width={850}
      styles={{ body: { padding: 0 } }}
      wrapClassName='rounded-3xl'
      centered
      destroyOnClose
    >
      <style>{`
        /* Override Ant Design Default Modal Padding */
        .ant-modal-content {
          padding: 0 !important;
          border-radius: 20px !important;
          overflow: hidden !important;
        }

        .terms-custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .terms-custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .terms-custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 20px;
        }
        .terms-custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #94a3b8;
        }
        /* Mobile optimization */
        @media (max-width: 768px) {
          .ant-modal {
             margin: 16px;
             max-width: calc(100vw - 32px);
          }
        }
      `}</style>

      {/* Header */}
      <div
        className='relative overflow-hidden p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6'
        style={{ background: 'linear-gradient(to bottom right, #172554, #1e3a8a, #312e81)' }}
      >
        <div className='absolute top-0 left-10 -mt-10 w-48 h-48 rounded-full bg-white/20 blur-2xl pointer-events-none'></div>
        <div className='absolute bottom-0 right-10 -mb-10 w-40 h-40 rounded-full bg-blue-300/30 blur-2xl pointer-events-none'></div>

        <div className='flex items-center gap-4 relative z-10 w-full md:w-auto'>
          <div className='flex-shrink-0 p-3.5 bg-white/20 rounded-2xl backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)] border border-white/20'>
            {step === 1 ? (
              <FileText className='w-7 h-7 text-white' strokeWidth={1.5} />
            ) : (
              <ShieldCheck className='w-7 h-7 text-white' strokeWidth={1.5} />
            )}
          </div>
          <div>
            <h2 className='text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight'>
              {step === 1 ? (
                <IntlMessages id='app.global.label.termCondition' />
              ) : (
                <IntlMessages id='app.global.label.privacyPolicy' />
              )}
            </h2>
            <p className='text-blue-50 text-sm md:text-base mt-1.5 font-normal'>
              <IntlMessages id='app.global.label.privacyPolicyDescription' />
            </p>
          </div>
        </div>

        <div className='flex flex-col gap-3 items-center relative z-10 w-full md:w-auto'>
          {/* Step indicator */}
          <div className='flex items-center gap-2'>
            <div
              className={`h-2 rounded-full transition-all duration-300 ${step === 1 ? 'w-6 bg-white' : 'w-2 bg-white/40'}`}
            />
            <div
              className={`h-2 rounded-full transition-all duration-300 ${step === 2 ? 'w-6 bg-white' : 'w-2 bg-white/40'}`}
            />
          </div>

          {/* Language switcher */}
          <Dropdown menu={{ items: langItems }} trigger={['click']} placement='bottomRight'>
            <button className='flex items-center justify-between gap-3 bg-black/10 hover:bg-white/10 px-4 py-2 md:py-2.5 rounded-xl backdrop-blur-md border border-white/10 shadow-inner w-full md:w-auto transition-all duration-300 cursor-pointer'>
              <div className='flex items-center gap-2 text-white'>
                <Globe className='w-5 h-5 opacity-90' />
                <span className='text-sm font-medium'>{POLICY_LANG_LABELS[lang]}</span>
              </div>
              <ChevronDown className='w-4 h-4 text-white/80' />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Content */}
      <div className='bg-white px-6 py-6 md:px-10 md:py-8 max-h-[50vh] overflow-y-auto terms-custom-scrollbar'>
        <AnimatePresenceCompat>
          {step === 1 ? (
            <motion.div
              key='step1'
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <div className={proseClass} dangerouslySetInnerHTML={{ __html: termsContent }} />
            </motion.div>
          ) : (
            <motion.div
              key='step2'
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.25 }}
            >
              <div className={proseClass} dangerouslySetInnerHTML={{ __html: privacyContent }} />
            </motion.div>
          )}
        </AnimatePresenceCompat>
      </div>

      {/* Footer */}
      <div className='px-6 py-4 md:py-5 bg-white border-t border-slate-100 relative z-10'>
        {/* Checkboxes */}
        <div className='mb-4 space-y-3'>
          {step === 1 ? (
            <label className='flex items-start gap-3 cursor-pointer group'>
              <input
                type='checkbox'
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className='mt-0.5 w-4 h-4 rounded border-slate-300 cursor-pointer accent-blue-600'
              />
              <span className='text-sm text-slate-600 group-hover:text-slate-800 transition-colors select-none'>
                <IntlMessages id='app.global.label.acceptTermsCheckbox' />
              </span>
            </label>
          ) : (
            <>
              <label className='flex items-start gap-3 cursor-pointer group'>
                <input
                  type='checkbox'
                  checked={acknowledgedPrivacy}
                  onChange={(e) => setAcknowledgedPrivacy(e.target.checked)}
                  className='mt-0.5 w-4 h-4 rounded border-slate-300 cursor-pointer accent-blue-600'
                />
                <span className='text-sm text-slate-600 group-hover:text-slate-800 transition-colors select-none'>
                  <IntlMessages id='app.global.label.acknowledgePrivacyCheckbox' />
                </span>
              </label>
            </>
          )}
        </div>

        {/* Action buttons */}
        <div className='flex flex-col-reverse md:flex-row items-center justify-between gap-3 md:gap-4'>
          {/* <button
            onClick={handleDecline}
            className='w-full md:w-auto px-6 py-3 md:py-2.5 rounded-xl text-slate-500 font-semibold hover:bg-slate-100 hover:text-slate-800 transition-colors flex items-center justify-center gap-2'
          >
            <X className='w-5 h-5' strokeWidth={2.5} />
            <IntlMessages id='app.global.label.decline' />
          </button> */}
          <div></div>

          <div className='flex w-full md:w-auto gap-3'>
            {step === 2 && (
              <button
                onClick={handleBackStep}
                className='flex-1 md:flex-none px-6 py-3 md:py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center justify-center gap-2'
              >
                <ChevronLeft className='w-5 h-5' strokeWidth={2.5} />
                <IntlMessages id='app.global.bt.back' />
              </button>
            )}
            {step === 1 ? (
              <button
                onClick={handleNextStep}
                disabled={!termsAccepted}
                className='flex-1 md:flex-none px-8 py-3 md:py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:shadow-none text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-600/40 transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-md transition-all duration-200 flex items-center justify-center gap-2'
              >
                <IntlMessages id='app.global.bt.next' />
                <ChevronRight className='w-5 h-5' strokeWidth={2.5} />
              </button>
            ) : (
              <button
                onClick={handleAccept}
                disabled={!acknowledgedPrivacy}
                className='flex-1 md:flex-none px-8 py-3 md:py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:shadow-none active:bg-emerald-800 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-600/40 transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-md transition-all duration-200 flex items-center justify-center gap-2'
              >
                <Check className='w-5 h-5' strokeWidth={2.5} />
                <IntlMessages id='app.global.label.accept' />
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyAndTermConditionModal;
